<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useBranding } from 'shared/composables/useBranding';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { parseBoolean } from '@chatwoot/utils';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import ConversationPreferences from './ConversationPreferences.vue';
import MessageSignature from './MessageSignature.vue';
import FontSize from './FontSize.vue';
import UserLanguageSelect from './UserLanguageSelect.vue';
import ChangePassword from './ChangePassword.vue';
import NotificationPreferences from './NotificationPreferences.vue';
import AudioNotifications from './AudioNotifications.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import AccessToken from './AccessToken.vue';
import MfaSettingsCard from './MfaSettingsCard.vue';
import ActiveSessions from './ActiveSessions.vue';
import Policy from 'dashboard/components/policy.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export default {
  components: {
    MessageSignature,
    ConversationPreferences,
    SectionLayout,
    FontSize,
    UserLanguageSelect,
    UserProfilePicture,
    Policy,
    UserBasicDetails,
    RadioCard,
    ChangePassword,
    NotificationPreferences,
    AudioNotifications,
    AccessToken,
    MfaSettingsCard,
    ActiveSessions,
    Icon,
  },
  setup() {
    const { isEditorHotKeyEnabled, updateUISettings } = useUISettings();
    const { currentFontSize, updateFontSize } = useFontSize();
    const { replaceInstallationName } = useBranding();

    return {
      currentFontSize,
      updateFontSize,
      isEditorHotKeyEnabled,
      updateUISettings,
      replaceInstallationName,
    };
  },
  data() {
    return {
      avatarFile: '',
      avatarUrl: '',
      name: '',
      displayName: '',
      email: '',
      messageSignature: '',
      hotKeys: [
        {
          key: 'enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-enter.svg',
          darkImage: '/assets/images/dashboard/profile/hot-key-enter-dark.svg',
        },
        {
          key: 'cmd_enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter.svg',
          darkImage:
            '/assets/images/dashboard/profile/hot-key-ctrl-enter-dark.svg',
        },
      ],
      notificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      audioNotificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      profileSections: [
        {
          href: '#profile-personal',
          label: 'Dados pessoais',
          icon: 'i-lucide-user-round',
        },
        {
          href: '#profile-interface',
          label: 'Interface',
          icon: 'i-lucide-monitor-cog',
        },
        {
          href: '#profile-messages',
          label: 'Mensagens',
          icon: 'i-lucide-message-square-text',
        },
        {
          href: '#profile-security',
          label: 'Segurança',
          icon: 'i-lucide-shield-check',
        },
        {
          href: '#profile-notifications',
          label: 'Notificações',
          icon: 'i-lucide-bell',
        },
        {
          href: '#profile-developer',
          label: 'Acesso à API',
          icon: 'i-lucide-key-round',
        },
      ],
      profileCopy: {
        navigationLabel: 'Seções das configurações do perfil',
        personalTitle: 'Dados pessoais',
        personalDescription:
          'Atualize sua foto e as informações usadas para identificar você no atendimento.',
      },
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    apiAndWebhooksEnabled() {
      if (!this.isOnChatwootCloud) return true;

      return this.currentUser.accounts.some(
        account => account.api_and_webhooks
      );
    },
    accessTokenDescription() {
      if (!this.apiAndWebhooksEnabled) {
        return this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.PAID_PLAN_NOTE');
      }

      return this.replaceInstallationName(
        this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE')
      );
    },
    isMfaEnabled() {
      return parseBoolean(window.chatwootConfig?.isMfaEnabled);
    },
  },
  mounted() {
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  methods: {
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.displayName = this.currentUser.display_name;
      this.messageSignature = this.currentUser.message_signature;
    },
    async dispatchUpdate(payload, successMessage, errorMessage) {
      let alertMessage = '';
      try {
        await this.$store.dispatch('updateProfile', payload);
        alertMessage = successMessage;

        return true; // return the value so that the status can be known
      } catch (error) {
        alertMessage = parseAPIErrorResponse(error) || errorMessage;

        return false; // return the value so that the status can be known
      } finally {
        useAlert(alertMessage);
      }
    },
    async updateProfile(userAttributes) {
      const { name, email, displayName } = userAttributes;
      const hasEmailChanged = this.currentUser.email !== email;
      this.name = name || this.name;
      this.email = email || this.email;
      this.displayName = displayName || this.displayName;

      const updatePayload = {
        name: this.name,
        email: this.email,
        displayName: this.displayName,
        avatar: this.avatarFile,
      };

      const success = await this.dispatchUpdate(
        updatePayload,
        hasEmailChanged
          ? this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
          : this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('RESET_PASSWORD.API.ERROR_MESSAGE')
      );

      if (hasEmailChanged && success) clearCookiesOnLogout();
    },
    async updateSignature(signature, layout = 'same_line') {
      const payload = {
        message_signature: signature,
        ui_settings: {
          ...this.currentUser.ui_settings,
          message_signature_layout:
            layout === 'next_line' ? 'next_line' : 'same_line',
        },
      };
      let successMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'
      );
      let errorMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'
      );

      await this.dispatchUpdate(payload, successMessage, errorMessage);
    },
    updateProfilePicture({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async deleteProfilePicture() {
      try {
        await this.$store.dispatch('deleteAvatar');
        this.avatarUrl = '';
        this.avatarFile = '';
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
      }
    },
    toggleHotKey(key) {
      this.hotKeys = this.hotKeys.map(hotKey =>
        hotKey.key === key ? { ...hotKey, active: !hotKey.active } : hotKey
      );
      this.updateUISettings({ editor_message_key: key });
      useAlert(this.$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.UPDATE_SUCCESS'));
    },
    async onCopyToken(value) {
      if (!this.apiAndWebhooksEnabled) return;

      await copyTextToClipboard(value);
      useAlert(this.$t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
    },
    async resetAccessToken() {
      if (!this.apiAndWebhooksEnabled) return;

      const success = await this.$store.dispatch('resetAccessToken');
      if (success) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_SUCCESS'));
      } else {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div
    class="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 pb-12 pt-6 sm:px-6"
  >
    <div
      class="relative overflow-hidden flex flex-col gap-4 rounded-3xl border border-n-weak bg-n-solid-1 p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-7"
    >
      <div class="flex min-w-0 items-center gap-4">
        <span
          class="grid size-12 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <Icon icon="i-lucide-user-round-cog" class="size-5" />
        </span>
        <div class="min-w-0">
          <h1 class="mb-1 text-2xl font-semibold text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.TITLE') }}
          </h1>
          <p class="mb-0 max-w-2xl text-sm leading-5 text-n-slate-11">
            {{
              replaceInstallationName(
                $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')
              )
            }}
          </p>
        </div>
      </div>
      <span
        class="inline-flex items-center gap-2 self-start rounded-xl border border-n-weak bg-n-slate-2 px-3 py-2 text-xs font-medium text-n-slate-11 sm:self-auto"
      >
        <Icon icon="i-lucide-shield-check" class="size-4 text-n-teal-10" />
        {{ currentUser.email }}
      </span>
    </div>
    <div
      class="grid min-w-0 gap-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:items-start"
    >
      <aside class="lg:sticky lg:top-6">
        <nav
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-2 shadow-sm"
          :aria-label="profileCopy.navigationLabel"
        >
          <a
            v-for="section in profileSections"
            :key="section.href"
            :href="section.href"
            class="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
          >
            <span
              class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-10 group-hover:text-n-brand"
            >
              <Icon :icon="section.icon" class="size-4" />
            </span>
            {{ section.label }}
          </a>
        </nav>
      </aside>
      <div class="flex min-w-0 flex-col gap-4">
        <SectionLayout
          id="profile-personal"
          icon="i-lucide-user-round"
          :title="profileCopy.personalTitle"
          :description="profileCopy.personalDescription"
          class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
        >
          <div
            class="grid gap-7 md:grid-cols-[180px_minmax(0,1fr)] md:items-start"
          >
            <UserProfilePicture
              :src="avatarUrl"
              :name="name"
              @change="updateProfilePicture"
              @delete="deleteProfilePicture"
            />
            <UserBasicDetails
              :name="name"
              :display-name="displayName"
              :email="email"
              :email-enabled="!globalConfig.disableUserProfileUpdate"
              @update-user="updateProfile"
            />
          </div>
        </SectionLayout>
        <SectionLayout
          id="profile-interface"
          icon="i-lucide-monitor-cog"
          class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
          :title="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.TITLE')"
          :description="
            replaceInstallationName(
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')
            )
          "
        >
          <div class="flex flex-col gap-6 items-start">
            <FontSize
              :value="currentFontSize"
              :label="
                $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.TITLE')
              "
              :description="
                $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.NOTE')
              "
              @change="updateFontSize"
            />
            <UserLanguageSelect
              :label="
                $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.TITLE')
              "
              :description="
                $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.NOTE')
              "
            />
          </div>
        </SectionLayout>
        <ConversationPreferences />
        <SectionLayout
          id="profile-messages"
          icon="i-lucide-signature"
          class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
          :title="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
          :description="
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')
          "
        >
          <MessageSignature
            :message-signature="messageSignature"
            @update-signature="updateSignature"
          />
        </SectionLayout>
        <SectionLayout
          icon="i-lucide-corner-down-left"
          class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
          :title="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.NOTE')"
        >
          <div
            class="flex flex-col justify-between w-full gap-5 sm:gap-4 sm:flex-row"
          >
            <RadioCard
              v-for="hotKey in hotKeys"
              :id="hotKey.key"
              :key="hotKey.key"
              :label="hotKey.title"
              :description="hotKey.description"
              :is-active="isEditorHotKeyEnabled(hotKey.key)"
              class="sm:flex-1"
              @select="toggleHotKey"
            >
              <img
                :src="hotKey.lightImage"
                :alt="`Light themed image for ${hotKey.title}`"
                class="block object-cover w-full dark:hidden"
              />
              <img
                :src="hotKey.darkImage"
                :alt="`Dark themed image for ${hotKey.title}`"
                class="hidden object-cover w-full dark:block"
              />
            </RadioCard>
          </div>
        </SectionLayout>
        <SectionLayout
          v-if="!globalConfig.disableUserProfileUpdate"
          id="profile-security"
          icon="i-lucide-lock-keyhole"
          class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
          :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
          description=""
        >
          <ChangePassword />
        </SectionLayout>
        <SectionLayout
          icon="i-lucide-shield-check"
          class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
          :title="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.NOTE')"
        >
          <MfaSettingsCard :is-mfa-available="isMfaEnabled" />
        </SectionLayout>
        <SectionLayout
          icon="i-lucide-laptop-minimal-check"
          class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
          :title="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.TITLE')"
          :description="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.NOTE')"
        >
          <ActiveSessions />
        </SectionLayout>
        <Policy :permissions="audioNotificationPermissions">
          <SectionLayout
            id="profile-notifications"
            icon="i-lucide-volume-2"
            class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
            :title="
              $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TITLE')
            "
            :description="
              $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
            "
          >
            <AudioNotifications />
          </SectionLayout>
        </Policy>
        <Policy :permissions="notificationPermissions">
          <SectionLayout
            icon="i-lucide-bell-ring"
            class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
            :title="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TITLE')"
            description=""
          >
            <NotificationPreferences />
          </SectionLayout>
        </Policy>
        <SectionLayout
          id="profile-developer"
          icon="i-lucide-key-round"
          class="!mt-0 rounded-2xl border border-n-weak bg-n-solid-1 !px-5 !py-6 sm:!px-6"
          :title="$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
          :description="accessTokenDescription"
        >
          <AccessToken
            :value="currentUser.access_token"
            :disabled="!apiAndWebhooksEnabled"
            @on-copy="onCopyToken"
            @on-reset="resetAccessToken"
          />
        </SectionLayout>
      </div>
    </div>
  </div>
</template>
